import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { keyframes } from '@mui/material/styles';
import type React from 'react';

interface DialogSkeletonProps {
  /** Number of content rows (default 3) */
  rows?: number;
  /** Show title skeleton when the dialog has no header title (default true) */
  hasTitle?: boolean;
  /** 'text' shows rectangular bars of varying widths; 'form' shows label + input pairs */
  variant?: 'text' | 'form';
}

/** Rotating width pattern for text skeletons to look natural */
const TEXT_WIDTHS = ['100%', '80%', '60%', '90%', '70%', '50%'];

/** Pre-built stable keys to avoid array-index-as-key lint issues */
const ROW_KEYS = Array.from({ length: 10 }, (_, i) => `skeleton-row-${i}`);

/** Mockup shimmer animation — matches .skeleton from modal-mockup.html */
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

/** Base skeleton sx — grey-600 → grey-500 → grey-600 gradient shimmer */
const skeletonSx = {
  borderRadius: '4px',
  background: (theme: Theme) =>
    `linear-gradient(90deg, ${theme.palette.grey[600]} 25%, ${theme.palette.grey[500]} 50%, ${theme.palette.grey[600]} 75%)`,
  backgroundSize: '400px 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
} as const;

const DialogSkeleton: React.FC<DialogSkeletonProps> = ({ rows = 3, hasTitle = true, variant = 'text' }) => {
  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {hasTitle && (
        <Box
          sx={{
            ...skeletonSx,
            height: 20,
            width: '35%',
            mb: 2,
          }}
        />
      )}

      {variant === 'text' &&
        ROW_KEYS.slice(0, rows).map((key, i) => (
          <Box
            key={key}
            sx={{
              ...skeletonSx,
              width: TEXT_WIDTHS[i % TEXT_WIDTHS.length],
              height: 14,
              mb: 1,
            }}
          />
        ))}

      {variant === 'form' &&
        ROW_KEYS.slice(0, rows).map((key, i) => (
          <Box key={key} sx={{ mb: 2 }}>
            {/* Label skeleton */}
            <Box
              sx={{
                ...skeletonSx,
                width: `${60 + ((i * 20) % 60)}px`,
                height: 12,
                mb: 0.75,
              }}
            />
            {/* Input field skeleton */}
            <Box
              sx={{
                ...skeletonSx,
                width: '100%',
                height: 36,
              }}
            />
          </Box>
        ))}
    </Box>
  );
};

export type { DialogSkeletonProps };
/** Exported for custom per-dialog skeleton layouts */
export { DialogSkeleton, shimmer, skeletonSx };
export default DialogSkeleton;
